import { lazy } from 'react';
import { useAuth } from './contexts/AuthContext';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const Inbox = lazy(() => import('./pages/inbox/Inbox'));
const HostDashboard = lazy(() => import('./pages/host/HostDashboard'));
const EquipmentList = lazy(() => import('./pages/equipment/EquipmentList'));
const EquipmentForm = lazy(() => import('./pages/equipment/EquipmentForm'));
const AnalyticalMethodList = lazy(() => import('./pages/analytical-methods/AnalyticalMethodList'));
const AnalyticalMethodForm = lazy(() => import('./pages/analytical-methods/AnalyticalMethodForm'));
const DocumentList = lazy(() => import('./pages/documents/DocumentList'));
const DocumentForm = lazy(() => import('./pages/documents/DocumentForm'));
const CleaningAgentList = lazy(() => import('./pages/cleaning-agents/CleaningAgentList'));
const CleaningAgentForm = lazy(() => import('./pages/cleaning-agents/CleaningAgentForm'));
const HbelList = lazy(() => import('./pages/hbels/HbelList'));
const HbelForm = lazy(() => import('./pages/hbels/HbelForm'));
const ProductList = lazy(() => import('./pages/products/ProductList'));
const ProductForm = lazy(() => import('./pages/products/ProductForm'));
const NpaList = lazy(() => import('./pages/new-product-assessment/NpaList'));
const NpaWizard = lazy(() => import('./pages/new-product-assessment/NpaWizard'));
const FmecaList = lazy(() => import('./pages/fmeca-assessment/FmecaList'));
const FmecaForm = lazy(() => import('./pages/fmeca-assessment/FmecaForm'));
const ProtocolList = lazy(() => import('./pages/validation-protocol/ProtocolList'));
const ProtocolWizard = lazy(() => import('./pages/validation-protocol/ProtocolWizard'));
const InspectorList = lazy(() => import('./pages/visual-inspection/InspectorList'));
const InspectorForm = lazy(() => import('./pages/visual-inspection/InspectorForm'));
const QualificationSessionList = lazy(() => import('./pages/visual-inspection/QualificationSessionList'));
const QualificationSessionForm = lazy(() => import('./pages/visual-inspection/QualificationSessionForm'));
const VrlStudyList = lazy(() => import('./pages/visual-inspection/VrlStudyList'));
const VrlStudyForm = lazy(() => import('./pages/visual-inspection/VrlStudyForm'));
const VrlLibraryList = lazy(() => import('./pages/visual-inspection/VrlLibraryList'));
const VisualSuitability = lazy(() => import('./pages/visual-inspection/VisualSuitability'));
const RankingStudyList = lazy(() => import('./pages/visual-inspection/RankingStudyList'));
const RankingStudyForm = lazy(() => import('./pages/visual-inspection/RankingStudyForm'));
const VisualInspectionTrends = lazy(() => import('./pages/visual-inspection/VisualInspectionTrends'));
const RiskList = lazy(() => import('./pages/risk-register/RiskList'));
const RiskDetail = lazy(() => import('./pages/risk-register/RiskDetail'));
const MyActions = lazy(() => import('./pages/risk-register/MyActions'));
const MitigationActionDetail = lazy(() => import('./pages/risk-register/MitigationActionDetail'));
const CleaningLimitCalculator = lazy(() => import('./pages/limit-calculator/CleaningLimitCalculator'));
const TeamList = lazy(() => import('./pages/team/TeamList'));
const RolesPage = lazy(() => import('./pages/roles/RolesPage'));
const AuditLog = lazy(() => import('./pages/audit/AuditLog'));
const EmailTemplatesPage = lazy(() => import('./pages/email-templates/EmailTemplatesPage'));
const Profile = lazy(() => import('./pages/account/Profile'));
const OrganisationList = lazy(() => import('./pages/host/OrganisationList'));
const HostTeamList = lazy(() => import('./pages/host/HostTeamList'));
const HostSettings = lazy(() => import('./pages/host/HostSettings'));
const HostEmailTemplates = lazy(() => import('./pages/host/HostEmailTemplates'));
const EmailLog = lazy(() => import('./pages/host/EmailLog'));
const TenantSettings = lazy(() => import('./pages/settings/TenantSettings'));

function DashboardSwitch() {
  const { user } = useAuth();
  return user?.isHost ? <HostDashboard /> : <Dashboard />;
}

const AppRoutes = [
  { index: true, element: <DashboardSwitch /> },
  { path: '/inbox', element: <Inbox /> },
  { path: '/equipment', element: <EquipmentList /> },
  { path: '/equipment/new', element: <EquipmentForm /> },
  { path: '/equipment/:id', element: <EquipmentForm /> },
  { path: '/analytical-methods', element: <AnalyticalMethodList /> },
  { path: '/analytical-methods/new', element: <AnalyticalMethodForm /> },
  { path: '/analytical-methods/:id', element: <AnalyticalMethodForm /> },
  { path: '/documents', element: <DocumentList /> },
  { path: '/documents/new', element: <DocumentForm /> },
  { path: '/documents/:id', element: <DocumentForm /> },
  { path: '/cleaning-agents', element: <CleaningAgentList /> },
  { path: '/cleaning-agents/new', element: <CleaningAgentForm /> },
  { path: '/cleaning-agents/:id', element: <CleaningAgentForm /> },
  { path: '/hbels', element: <HbelList /> },
  { path: '/hbels/new', element: <HbelForm /> },
  { path: '/hbels/:id', element: <HbelForm /> },
  { path: '/products', element: <ProductList /> },
  { path: '/products/new', element: <ProductForm /> },
  { path: '/products/:id', element: <ProductForm /> },
  { path: '/new-product-assessment', element: <NpaList /> },
  { path: '/new-product-assessment/new', element: <NpaWizard /> },
  { path: '/new-product-assessment/:id', element: <NpaWizard /> },
  { path: '/fmeca-assessment', element: <FmecaList /> },
  { path: '/fmeca-assessment/new', element: <FmecaForm /> },
  { path: '/fmeca-assessment/:id', element: <FmecaForm /> },
  { path: '/validation-protocols', element: <ProtocolList /> },
  { path: '/validation-protocols/:id', element: <ProtocolWizard /> },
  { path: '/visual-inspection/inspectors', element: <InspectorList /> },
  { path: '/visual-inspection/inspectors/new', element: <InspectorForm /> },
  { path: '/visual-inspection/inspectors/:id', element: <InspectorForm /> },
  { path: '/visual-inspection/qualifications', element: <QualificationSessionList /> },
  { path: '/visual-inspection/qualifications/new', element: <QualificationSessionForm /> },
  { path: '/visual-inspection/qualifications/:id', element: <QualificationSessionForm /> },
  { path: '/visual-inspection/vrl-studies', element: <VrlStudyList /> },
  { path: '/visual-inspection/vrl-studies/new', element: <VrlStudyForm /> },
  { path: '/visual-inspection/vrl-studies/:id', element: <VrlStudyForm /> },
  { path: '/visual-inspection/vrl-library', element: <VrlLibraryList /> },
  { path: '/visual-inspection/suitability', element: <VisualSuitability /> },
  { path: '/visual-inspection/ranking-studies', element: <RankingStudyList /> },
  { path: '/visual-inspection/ranking-studies/new', element: <RankingStudyForm /> },
  { path: '/visual-inspection/ranking-studies/:id', element: <RankingStudyForm /> },
  { path: '/visual-inspection/trends', element: <VisualInspectionTrends /> },
  { path: '/risk-register', element: <RiskList /> },
  { path: '/risk-register/:id', element: <RiskDetail /> },
  { path: '/my-actions', element: <MyActions /> },
  { path: '/mitigation-actions/:actionId', element: <MitigationActionDetail /> },
  { path: '/limit-calculator', element: <CleaningLimitCalculator /> },
  { path: '/email-templates', element: <EmailTemplatesPage /> },
  { path: '/team', element: <TeamList /> },
  { path: '/roles', element: <RolesPage /> },
  { path: '/audit-log', element: <AuditLog /> },
  { path: '/profile', element: <Profile /> },
  { path: '/org-settings', element: <TenantSettings /> },
  { path: '/organisations', element: <OrganisationList /> },
  { path: '/host-team', element: <HostTeamList /> },
  { path: '/host-email-templates', element: <HostEmailTemplates /> },
  { path: '/email-log', element: <EmailLog /> },
  { path: '/settings', element: <HostSettings /> },
];

export default AppRoutes;
